import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { useAuth } from '../context/AuthContext';
import { eventService } from '../services/eventService';
import { ProjectCreditAllocation, subscriptionService } from '../services/subscriptionService';
import {
  getCreditShortfall,
  getTopUpCreditAmount,
  type InsufficientCreditsPayload,
} from '../utils/creditGate';

import { BillingModal } from './BillingModal';
import { CreditRefillNotice } from './CreditRefillNotice';
import './CreditDisplay.css';

const formatCredits = (n: number): string => Number.isInteger(n) ? n.toString() : n.toFixed(2);
const formatMaybeCredits = (n?: number | null): string => (
  n === null || n === undefined ? '--' : formatCredits(Math.max(0, n))
);

const NEXT_PLAN_LABEL_BY_TIER: Record<string, string> = {
  free: 'standard',
  trial: 'standard',
  standard: 'plus',
  standard_india: 'plus',
  byok: 'plus',
  plus: 'pro',
  pro: 'unlimited',
};

const normalizeTier = (tier?: string | null): string => (tier || 'free').trim().toLowerCase();

const getNextPlanLabel = (tier?: string | null) => NEXT_PLAN_LABEL_BY_TIER[normalizeTier(tier)];

const CreditWarningToast: React.FC<{
  shortfall?: number;
  onDismiss: () => void;
  onOpenBilling: (view: 'credits' | 'plans') => void;
}> = ({ shortfall, onDismiss, onOpenBilling }) => {

  return (
    <>
      <div className="credit-warning-toast" role="status">
        <span className="credit-warning-toast__icon">⚠️</span>
        <span className="credit-warning-toast__text">
          Insufficient credits{shortfall ? ` (${formatCredits(shortfall)} more needed)` : ''}
          <CreditRefillNotice />
        </span>
        <button
          className="credit-warning-toast__btn credit-warning-toast__btn--add app-action-btn app-action-btn--warning"
          onClick={() => onOpenBilling('credits')}
          type="button"
        >
          Add Credits
        </button>
        <button
          className="credit-warning-toast__btn credit-warning-toast__btn--upgrade app-action-btn app-action-btn--upgrade"
          onClick={() => onOpenBilling('plans')}
          type="button"
        >
          Upgrade
        </button>
        <button className="credit-warning-toast__close" aria-label="Dismiss credit warning" onClick={onDismiss} type="button">
          ✕
        </button>
      </div>
    </>
  );
};

const ProjectCreditRow: React.FC<{
  allocation: ProjectCreditAllocation;
  isCurrent?: boolean;
}> = ({ allocation, isCurrent = false }) => {
  const remaining = allocation.is_unlimited
    ? 'Unlimited'
    : allocation.quota_required
    ? 'Quota not assigned'
    : `${formatMaybeCredits(allocation.ai_credits_remaining)} left`;
  const usage = allocation.is_unlimited
    ? `${formatCredits(allocation.ai_credits_used)} recorded usage`
    : allocation.ai_credit_quota === null || allocation.ai_credit_quota === undefined
    ? `${formatCredits(allocation.ai_credits_used)} used`
    : `${formatCredits(allocation.ai_credits_used)} used of ${formatCredits(allocation.ai_credit_quota)}`;

  return (
    <div className={`credit-project-row${isCurrent ? ' credit-project-row--current' : ''}`}>
      <div className="credit-project-row__title" title={allocation.project_title}>
        {allocation.project_title}
      </div>
      <div className="credit-project-row__meta">
        <span className={allocation.quota_required ? 'credit-project-row__missing' : ''}>
          {remaining}
        </span>
        <span>{usage}</span>
      </div>
      {allocation.manager_name && (
        <div className="credit-project-row__manager">
          Provided by {allocation.manager_name}
        </div>
      )}
    </div>
  );
};

interface CreditDisplayProps {
  currentProjectId?: string | null;
}

type CreditsUpdatePayload = {
  credits_balance?: number;
  triggered_by?: string;
  project_id?: string | null;
  reason?: string | null;
  ai_credit_quota?: number | null;
  ai_credits_used?: number | null;
  ai_credits_remaining?: number | null;
  ai_credit_quota_exhausted?: boolean | null;
  quota_required?: boolean | null;
};

const updateProjectCreditAllocation = (
  allocation: ProjectCreditAllocation,
  payload: CreditsUpdatePayload,
): ProjectCreditAllocation => ({
  ...allocation,
  ai_credit_quota: Object.prototype.hasOwnProperty.call(payload, 'ai_credit_quota')
    ? payload.ai_credit_quota ?? null
    : allocation.ai_credit_quota,
  ai_credits_used: payload.ai_credits_used ?? allocation.ai_credits_used,
  ai_credits_remaining: Object.prototype.hasOwnProperty.call(payload, 'ai_credits_remaining')
    ? payload.ai_credits_remaining ?? null
    : allocation.ai_credits_remaining,
  ai_credit_quota_exhausted: Boolean(payload.ai_credit_quota_exhausted),
  quota_required: Boolean(payload.quota_required),
});

const updateProjectCredits = (
  current: ProjectCreditAllocation[],
  payload: CreditsUpdatePayload,
): ProjectCreditAllocation[] => current.map((allocation) => {
  if (allocation.project_id !== payload.project_id) return allocation;

  return updateProjectCreditAllocation(allocation, payload);
});

const useCreditBalances = (
  userId?: string,
  fallbackCredits = 0,
  fallbackUnlimited = false,
): {
  credits: number;
  projectCredits: ProjectCreditAllocation[];
  isUnlimited: boolean;
} => {
  const [credits, setCredits] = useState<number>(fallbackCredits);
  const [projectCredits, setProjectCredits] = useState<ProjectCreditAllocation[]>([]);
  const [isUnlimited, setIsUnlimited] = useState(fallbackUnlimited);

  useEffect(() => {
    let active = true;
    setIsUnlimited(fallbackUnlimited);

    subscriptionService.getCurrentSubscription()
      .then((sub) => {
        if (!active) return;
        setCredits(sub.credits_balance);
        setIsUnlimited(Boolean(sub.is_unlimited));
        setProjectCredits(sub.project_credits || []);
      })
      .catch(() => {});

    return () => { active = false; };
  }, [fallbackUnlimited, userId]);

  useEffect(() => {
    const unsubCredits = eventService.subscribe('credits_update', (data: unknown) => {
      const payload = data as CreditsUpdatePayload;

      if (payload.triggered_by && payload.triggered_by !== userId) return;
      if (payload.credits_balance !== undefined) setCredits(payload.credits_balance);
      if (payload.project_id && payload.reason === 'developer_mode_token_usage') {
        setProjectCredits((current) => updateProjectCredits(current, payload));
      }
    });

    return () => { unsubCredits(); };
  }, [userId]);

  return { credits, projectCredits, isUnlimited };
};

const CurrentProjectCredit: React.FC<{ allocation?: ProjectCreditAllocation }> = ({ allocation }) => {
  if (!allocation) return null;

  return (
    <div className="credit-current-project">
      <span>This project</span>
      <strong>
        {allocation.is_unlimited
          ? 'Unlimited'
          : allocation.quota_required
          ? 'quota not assigned'
          : `${formatMaybeCredits(allocation.ai_credits_remaining)} left`}
      </strong>
    </div>
  );
};

const ProjectCreditBreakdown: React.FC<{
  allocations: ProjectCreditAllocation[];
  currentProjectId?: string | null;
}> = ({ allocations, currentProjectId }) => {
  if (allocations.length === 0) return null;

  return (
    <details className="credit-breakdown">
      <summary>
        <span>Project credits</span>
        <small>{allocations.length}</small>
      </summary>
      <div className="credit-breakdown__list">
        {allocations.map((allocation) => (
          <ProjectCreditRow
            key={allocation.project_id}
            allocation={allocation}
            isCurrent={allocation.project_id === currentProjectId}
          />
        ))}
      </div>
    </details>
  );
};

export const CreditDisplay: React.FC<CreditDisplayProps> = ({ currentProjectId = null }) => {
  const { user } = useAuth();
  const [showBillingModal, setShowBillingModal] = useState(false);
  const subscriptionStatus = normalizeTier(user?.subscription_status || 'active');
  const authUnlimited = (
    normalizeTier(user?.subscription_tier) === 'unlimited'
    && ['active', 'cancel_pending'].includes(subscriptionStatus)
  );
  const { credits, projectCredits, isUnlimited: subscriptionUnlimited } = useCreditBalances(
    user?.id,
    user?.credits_balance || 0,
    authUnlimited,
  );
  const isUnlimited = subscriptionUnlimited;
  const currentProjectCredits = currentProjectId
    ? projectCredits.find((allocation) => allocation.project_id === currentProjectId)
    : null;
  const sortedProjectCredits = currentProjectCredits
    ? [
      currentProjectCredits,
      ...projectCredits.filter((allocation) => allocation.project_id !== currentProjectId),
    ]
    : projectCredits;

  return (
    <>
      <div className="credit-display-group">
        <div className="credit-badge">
          <span className="credit-label">Credits:</span>
          <span className="credit-count">{isUnlimited ? 'Unlimited' : formatCredits(credits)}</span>
          {!isUnlimited && (
            <button className="btn-add-credit" onClick={() => setShowBillingModal(true)} type="button">+</button>
          )}
        </div>
      </div>
      <CurrentProjectCredit allocation={currentProjectCredits || undefined} />
      <ProjectCreditBreakdown allocations={sortedProjectCredits} currentProjectId={currentProjectId} />
      {showBillingModal && !isUnlimited && (
        <BillingModal initialView="credits" onClose={() => setShowBillingModal(false)} />
      )}
    </>
  );
};

/**
 * Always-mounted listener that shows the insufficient-credits toast.
 * Must be rendered outside conditionally-mounted containers (e.g. dropdown menus).
 */
export const CreditWarningToastListener: React.FC = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [creditShortfall, setCreditShortfall] = useState<number | undefined>();
  const [billing, setBilling] = useState<{ view: 'credits' | 'plans'; amount: number } | null>(null);
  const dismissTimerRef = useRef<number | null>(null);
  const cancelDismissTimer = useCallback(() => {
    if (dismissTimerRef.current !== null) window.clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = null;
  }, []);
  const dismissWarning = useCallback(() => {
    cancelDismissTimer();
    setShowWarning(false);
  }, [cancelDismissTimer]);

  const openBilling = useCallback((view: 'credits' | 'plans') => {
    dismissWarning();
    setBilling({ view, amount: getTopUpCreditAmount({ shortfall: creditShortfall }) ?? 1 });
  }, [creditShortfall, dismissWarning]);
  const closeBilling = useCallback(() => {
    setBilling(null);
    dismissWarning();
  }, [dismissWarning]);

  useEffect(() => {
    const unsub = eventService.subscribe('insufficient_credits', (data: unknown) => {
      const payload = data as InsufficientCreditsPayload;

      if (dismissTimerRef.current !== null) window.clearTimeout(dismissTimerRef.current);
      setCreditShortfall(getCreditShortfall(payload));
      setShowWarning(true);
      dismissTimerRef.current = window.setTimeout(() => {
        dismissTimerRef.current = null;
        setShowWarning(false);
      }, 15_000);
    });

    return () => {
      unsub();
      if (dismissTimerRef.current !== null) window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    };
  }, []);

  // Billing belongs to the persistent listener, not the expiring notification.
  return (
    <>
      {showWarning && !billing && ReactDOM.createPortal(
        <CreditWarningToast
          shortfall={creditShortfall}
          onDismiss={dismissWarning}
          onOpenBilling={openBilling}
        />,
        document.body,
      )}
      {billing && (
        <BillingModal
          initialView={billing.view}
          initialCreditAmount={billing.amount}
          onClose={closeBilling}
        />
      )}
    </>
  );
};


interface UpgradeButtonProps {
  onClick?: () => void;
}

export const UpgradeButton: React.FC<UpgradeButtonProps> = ({ onClick }) => {
  const { user } = useAuth();
  // /auth/me is the canonical bootstrap snapshot and BillingModal refreshes it
  // after a successful upgrade. Avoid a second subscription GET just to label
  // this navigation button.
  const subscriptionTier = normalizeTier(user?.subscription_tier);
  const nextPlanLabel = getNextPlanLabel(subscriptionTier);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();

      return;
    }

    setShowBillingModal(true);
  }, [onClick]);

  if (!nextPlanLabel) return null;

  return (
    <>
      <button
        aria-label={`Upgrade to ${nextPlanLabel}`}
        className="btn-upgrade-nav app-action-btn app-action-btn--upgrade"
        onClick={handleClick}
        title={`Upgrade to ${nextPlanLabel}`}
        type="button"
      >
        <span className="btn-upgrade-nav__main">
          Upgrade
        </span>
        <span className="btn-upgrade-nav__sub">
          <span className="btn-upgrade-nav__prefix">to</span>
          <span className="btn-upgrade-nav__plan">{nextPlanLabel}</span>
        </span>
        <span className="btn-upgrade-nav__arrow" aria-hidden="true"><i className="fa-sharp fa-solid fa-bolt"></i></span>
      </button>
      {!onClick && showBillingModal && (
        <BillingModal initialView="plans" onClose={() => setShowBillingModal(false)} />
      )}
    </>
  );
};
